import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import OfferCalendarMonthPicker from '../CalendarPicker/MonthPicker/OfferCalendarMonthPicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarSidebarDayPicker from './OfferCalendarSidebarDayPicker'
import { getCalendarMonths, getSelectedOfferPageDay, isFetchingCalendars } from 'selectors/multipleRoomBookingSelectors'
import OfferCalendarNotAvailableMessage from '../CalendarPicker/OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from '../CalendarPicker/OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarRoomSelect from '../CalendarPicker/CalendarRooms/OfferCalendarRoomSelect'
import OfferCalendarDurationSelect from '../CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import OfferCalendarFlightsToggle from '../CalendarPicker/OfferCalendarFlightsToggle'
import noop from 'lib/function/noop'
import OfferCalendarCapacitySelect from '../CalendarPicker/CalendarCapacity/OfferCalendarCapacitySelect'
import { nonNullable } from 'lib/array/arrayUtils'
import { isFlashHotel, isVillaOffer } from 'lib/offer/offerTypes'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import OfferCalendarLuxuryPlusToggle from 'loyalty/components/OfferCalendarLuxuryPlusToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferCalendarSidebarDatePickerFooter from './OfferCalendarSidebarDatePickerFooter'
import * as Analytics from 'analytics/analytics'

interface Props {
  currentOfferId: string;
  offer: App.Offer;
  airport: App.Airport;
  fetching?: boolean;
  flights?: boolean;
  checkIn?: moment.Moment;
  selectedDay?: App.CalendarDay;
  calendarError?: boolean;
  calendarAvailability: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  onBack?: () => void;
  onClose?: () => void;
  subFilters?: Array<OfferPageFilterTypes>;
  isLuxuryPlusEnabled: boolean;
}

const defaultSubfilters: Array<OfferPageFilterTypes> = ['duration', 'airport', 'room']
function OfferCalendarSidebarDatePicker(props: Props) {
  const {
    offer,
    fetching,
    flights,
    selectedDay,
    checkIn,
    calendarError,
    calendarAvailability,
    defaultMonth,
    onBack,
    onClose = noop,
    currentOfferId,
    subFilters = defaultSubfilters,
    isLuxuryPlusEnabled,
  } = props
  const { currentRegionHasFlights } = useContext(GeoContext)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  useEffect(() => {
    if (checkIn && selectedDay && !selectedDay.hotelAvailable) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [checkIn, dispatch, selectedDay, currentOfferId])

  useEffect(() => {
    if (selectedDay) {
      if (isFlashHotel(offer)) {
        Analytics.trackClientEvent({
          subject: 'availability_date',
          action: 'click',
          category: 'offer_funnel',
          type: 'interaction',
        })
      }
    }
  }, [selectedDay, offer])

  const onBackToMonth = useCallback(() => {
    scrollerRef.current?.scrollTo({ top: 0 })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    setMonth(undefined)
  }, [onUserAnalyticsAction, dispatch, currentOfferId])

  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly

  const hasSelectedMonth = selectedMonth !== undefined

  const capacities = useMemo(() => nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities)), [offer])

  const onModalBack = useCallback(() => {
    if (selectedMonth) {
      onBackToMonth()
    } else {
      onBack?.()
    }
  }, [onBack, onBackToMonth, selectedMonth])

  const onMonthSelect = useCallback((monthKey: string) => {
    scrollerRef.current?.scrollTo({ top: 0 })
    setMonth(monthKey)
    if (isFlashHotel(offer)) {
      Analytics.trackClientEvent({
        subject: 'availability_month',
        action: 'click',
        category: 'offer_funnel',
        type: 'interaction',
      })
    }
  }, [offer])

  const anyPackageHasMemberPrice = useMemo(() => offer.packages.some(pkg => pkg.memberPrice > 0), [offer.packages])
  useEffect(() => {
    if (anyPackageHasMemberPrice && isLuxuryPlusEnabled) {
      dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
    }
  }, [anyPackageHasMemberPrice, dispatch, isLuxuryPlusEnabled])

  const showBack = !!onBack || !!selectedMonth

  return (<>
    <ModalHeader
      title={hasSelectedMonth ? 'Select check-in dates' : 'Select check-in month'}
      onCloseButtonClick={onClose}
      onBackButtonClick={showBack ? onModalBack : undefined}
    >
      <Group direction="horizontal" gap={8} wrap="wrap">
        {subFilters.includes('capacity') && <OfferCalendarCapacitySelect variant="modal" capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
        {subFilters.includes('room') && <OfferCalendarRoomSelect variant="modal" />}
        {subFilters.includes('duration') && <OfferCalendarDurationSelect variant="modal" />}
        {flightsEnabled && subFilters.includes('airport') && <OfferCalendarFlightsToggle />}
        {isLuxuryPlusEnabled && offer.type === OFFER_TYPE_HOTEL && anyPackageHasMemberPrice && <OfferCalendarLuxuryPlusToggle />}
      </Group>
    </ModalHeader>
    <ModalBody ref={scrollerRef}>
      <LoadingIndicator floating visible={fetching && hasSelectedMonth} />
      <ModalContent>
        {!fetching && (calendarError || !flights) && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
        {!fetching && !calendarError && flights && !calendarAvailability && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
        {calendarAvailability && <>
          {hasSelectedMonth && !fetching && <OfferCalendarSidebarDayPicker
            onBackToMonth={onBackToMonth}
            initialMonth={selectedMonth}
            flightsOnly={offer.bundledWithFlightsOnly}
            contentRef={scrollerRef}
          />}
          {!hasSelectedMonth && <OfferCalendarMonthPicker
            onMonthSelect={onMonthSelect}
          />}
        </>}
      </ModalContent>
    </ModalBody>
    <OfferCalendarSidebarDatePickerFooter />
  </>)
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    currentOfferId: state.paginator?.currentOfferId,
    airport: state.airport,
    calendarAvailability: getCalendarMonths(state, appState).availableCount > 0,
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    fetching: isFetchingCalendars(state, appState),
    flights: state.flights,
    offer: state.offer,
    selectedDay: getSelectedOfferPageDay(state, appState),
    isLuxuryPlusEnabled: isLuxuryPlusEnabled(appState),
  }
})(OfferCalendarSidebarDatePicker)
